/**
 * Phase 6 cross-cutting PR 3 — `<BugReportModal>`.
 *
 * Modal scaffold for the bug-report intake form. 3 required fields:
 *   - title (text, maxlength 200)
 *   - description (textarea)
 *   - severity (4-enum: low | medium | high | critical)
 *
 * On submit, builds the privacy-redacted payload (via
 * `buildBugPayload` lib) and POSTs to `/api/bug-report` (server
 * endpoint already exists at api/index.js:6911).
 *
 * Success → 3000ms `--toast-bg-success` toast at bottom-center,
 *           modal closes (200ms fade).
 * Error   → inline `--danger` save-status chip above footer; submit
 *           re-enables; modal stays open with values intact.
 *
 * The diagnostic-context ⓘ panel under severity surfaces the
 * 5-key safe subset to the user (trust signal — telegraphs what's
 * transmitted).
 *
 * Sourced from:
 *   - `.claude/references/phase-6-cross-cutting-design/direction.md` §2
 *   - `.claude/references/phase-6-cross-cutting-design/HANDOFF.md` PR 3
 *   - vanilla `src/renderer/js/bug-report.js` (134 lines) for locked copy
 */

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuthFetch } from '../../hooks/useAuthFetch';
import { useAuthContext } from '../../contexts/AuthContext';
import { useAthleteContext } from '../../hooks/useAthleteContext';
import {
  buildBugPayload,
  type BugReportSeverity,
} from '../../lib/buildBugPayload';
import { DiagContextHint } from './DiagContextHint';
import styles from './BugReportModal.module.css';

interface BugReportModalProps {
  open: boolean;
  onClose: () => void;
}

const TITLE_MAX = 200;

/** Locked severity options — copy strings verbatim from vanilla. */
const SEVERITY_OPTIONS: { value: BugReportSeverity; label: string }[] = [
  { value: 'low', label: 'Low - Minor inconvenience' },
  { value: 'medium', label: 'Medium - Feature not working correctly' },
  { value: 'high', label: 'High - Blocks my workflow' },
  { value: 'critical', label: 'Critical - App is broken / data loss' },
];

export function BugReportModal({ open, onClose }: BugReportModalProps): JSX.Element | null {
  const fetchAuthed = useAuthFetch();
  const { user } = useAuthContext();
  const { raw: athleteContext } = useAthleteContext();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [severity, setSeverity] = useState<BugReportSeverity>('medium');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const titleInputRef = useRef<HTMLInputElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  // Focus title input on open + reset state when modal closes.
  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => titleInputRef.current?.focus());
    } else {
      // Reset form when fully closed so the next open is clean.
      setTitle('');
      setDescription('');
      setSeverity('medium');
      setError(null);
      setSubmitting(false);
    }
  }, [open]);

  // Esc + click-outside to close (matches vanilla bug-report.js).
  useEffect(() => {
    if (!open) return;
    const handleEsc = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && !submitting) onClose();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [open, submitting, onClose]);

  // Toast auto-dismiss after 3000ms (locked from vanilla).
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(timer);
  }, [toast]);

  const handleSubmit = async (): Promise<void> => {
    const trimmedTitle = title.trim();
    const trimmedDescription = description.trim();
    if (!trimmedTitle || !trimmedDescription) {
      setError('Please fill in both title and description.');
      return;
    }
    setError(null);
    setSubmitting(true);

    const payload = buildBugPayload({
      title: trimmedTitle,
      description: trimmedDescription,
      severity,
      athleteContext,
      userRole: user?.user_metadata?.role ?? 'athlete',
    });

    try {
      const res = await fetchAuthed('/api/bug-report', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}) as { error?: string });
        throw new Error(body?.error ?? `Submit failed (${res.status})`);
      }
      // Success path — close modal, show toast.
      onClose();
      setToast('Bug report submitted. Thank you!');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      setError(`Failed to submit: ${msg}`);
      setSubmitting(false);
    }
  };

  const handleBackdropMouseDown = (e: React.MouseEvent<HTMLDivElement>): void => {
    if (e.target === e.currentTarget && !submitting) onClose();
  };

  if (!open && !toast) return null;

  return (
    <>
      {/* B-33 — the backdrop is position: fixed and this modal is rendered by
          the bug-report trigger inside the sticky TopBar <header>. Portal it to
          document.body (like the toast below) so it is not a descendant of the
          bar: the bar carries transform: translateZ(0) for a stable compositor
          layer, and a transformed ancestor would otherwise become this fixed
          backdrop's containing block and strand it inside the bar. */}
      {open && typeof document !== 'undefined' ? (
        createPortal(
        <div
          className={styles.backdrop}
          onMouseDown={handleBackdropMouseDown}
          role="presentation"
        >
          <div
            ref={modalRef}
            className={styles.modal}
            role="dialog"
            aria-modal="true"
            aria-labelledby="bug-report-title"
          >
            <div className={styles.header}>
              <h2 id="bug-report-title" className={styles.title}>
                Report a bug
              </h2>
              <button
                type="button"
                className={styles.closeButton}
                onClick={onClose}
                disabled={submitting}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className={styles.body}>
              <label className={styles.field}>
                <span className={styles.label}>
                  Title <span className={styles.required}>*</span>
                </span>
                <input
                  ref={titleInputRef}
                  type="text"
                  className={styles.input}
                  placeholder="Brief summary of the issue"
                  value={title}
                  onChange={(e) => setTitle(e.target.value.slice(0, TITLE_MAX))}
                  maxLength={TITLE_MAX}
                  disabled={submitting}
                />
                <span className={styles.counter}>
                  {title.length}/{TITLE_MAX}
                </span>
              </label>

              <label className={styles.field}>
                <span className={styles.label}>
                  Description <span className={styles.required}>*</span>
                </span>
                <textarea
                  className={styles.textarea}
                  rows={4}
                  placeholder="What happened? What did you expect?"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  disabled={submitting}
                />
              </label>

              <label className={styles.field}>
                <span className={styles.label}>Severity</span>
                <select
                  className={styles.select}
                  value={severity}
                  onChange={(e) => setSeverity(e.target.value as BugReportSeverity)}
                  disabled={submitting}
                >
                  {SEVERITY_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </label>

              <DiagContextHint />

              {error ? (
                <div className={styles.error} role="alert">
                  {error}
                </div>
              ) : null}
            </div>

            <div className={styles.footer}>
              <button
                type="button"
                className={styles.cancelButton}
                onClick={onClose}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="button"
                className={styles.submitButton}
                onClick={() => void handleSubmit()}
                disabled={submitting}
              >
                {submitting ? 'Submitting…' : 'Submit report'}
              </button>
            </div>
          </div>
        </div>,
          document.body,
        )
      ) : null}

      {/* Portal the toast to document.body so it escapes any ancestor
          stacking context (e.g., TopBar's `position: sticky` at z-index
          5, which trapped the toast below the Fab at z-index 10 on
          mobile). At document.body the toast's z-index: 400 stacks
          correctly above all chrome (Fab z-10, BottomTab z-100). */}
      {toast && typeof document !== 'undefined'
        ? createPortal(
            <div className={styles.toast} role="status">
              {toast}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
